import type { ComponentType } from 'react';
import { ActionPoliciesHelpBody } from '@/features/action-policies/actionPoliciesHelp';
import { CostsHelpBody, CostsCoverageHelpBody, CostsTrendHelpBody, CostsAgentsHelpBody, CostsPerRunHelpBody, CostsUnusualHelpBody } from '@/features/costs/costsHelp';
import {
  ReadOutlined,
  ThunderboltOutlined,
  CodeOutlined,
  RobotOutlined,
  ToolOutlined,
  ApiOutlined,
  ExperimentOutlined,
  CheckSquareOutlined,
  PlayCircleOutlined,
  CloudServerOutlined,
  PushpinOutlined,
  FilePdfOutlined,
  SearchOutlined,
  BranchesOutlined,
  SafetyOutlined,
  AppstoreOutlined,
  WarningOutlined,
  GlobalOutlined,
  AreaChartOutlined,
  DollarCircleOutlined,
  CompressOutlined,
  ApartmentOutlined,
  FilterOutlined,
  HistoryOutlined,
  MessageOutlined,
  HeartOutlined,
  ProfileOutlined,
  EyeOutlined,
  LinkOutlined,
  PoweroffOutlined,
  CloudUploadOutlined,
  BulbOutlined,
  PlusOutlined,
  SortAscendingOutlined,
} from '@ant-design/icons';
import {
  HeroBlock,
  SectionTitle,
  StepFlow,
  ChainDiagram,
  CodeBlock,
  KeyValueList,
  Callout,
  RelatedTopics,
  HelpParagraph,
} from './HelpPrimitives';

// Help topic registry. Every topic is keyed by a string id (e.g. "knowledge.rag")
// that any HelpHotspot can reference. Content is grounded in actual source
// files cited in `docs/` and verified by parallel research agents — see
// commit log around the help-system work for the agent reports.

export interface HelpTopic {
  label: string;
  Body: ComponentType;
}

// ── Knowledge / RAG ────────────────────────────────────────────────────────

const KnowledgeRagBody = () => (
  <>
    <HeroBlock
      icon={<ReadOutlined />}
      title="Knowledge bases & RAG"
      tagline="A KB is a curated collection of PDFs, Markdown notes, and links indexed into Azure AI Search. At chat time, the agent runs a hybrid retrieval against the index and injects the top chunks into its prompt as grounding."
      accent="ink"
    />

    <HelpParagraph>
      Every source you upload goes through a chunking + embedding pipeline that lands in a shared
      Azure AI Search index named <code>enhanced-v1</code>. At runtime, the agent walks its{' '}
      <code>ai.AiAgentKnowledge</code> links, queries each KB in parallel, and ranks the merged
      chunks by relevance — so you never wonder "did the agent see the right document?".
    </HelpParagraph>

    <SectionTitle>Indexing — what happens when you upload</SectionTitle>
    <StepFlow
      steps={[
        {
          icon: <FilePdfOutlined />,
          title: 'File lands in blob storage',
          body: 'KnowledgePdfManager.Add() (or KnowledgeMarkdownManager.Add()) writes the file to Azure Blob Storage and persists a row with BlobId + DocumentId = "pdf_{Id}".',
        },
        {
          icon: <BranchesOutlined />,
          title: 'Document is chunked',
          body: 'DocumentChunkingService splits the file into ~500-token chunks with 100-token overlap (using the GPT-4o tokenizer). Each chunk inherits filename, page number, and YAML frontmatter for citations.',
        },
        {
          icon: <CloudServerOutlined />,
          title: 'Chunks are embedded + upserted',
          body: 'Each chunk is embedded via the configured embedding model and pushed to the index with MerchantId, KnowledgeId, content, and contentVector (HNSW-indexed, cosine).',
          accent: 'violet',
        },
        {
          icon: <SearchOutlined />,
          title: 'Marked indexed in the UI',
          body: 'The KnowledgePdf row\'s DocumentId is non-null once the upsert completes — the "Indexed" pill in the Sources tab reads that field directly.',
          accent: 'green',
        },
      ]}
    />

    <SectionTitle>Retrieval — what happens at chat time</SectionTitle>
    <ChainDiagram
      nodes={[
        { icon: <RobotOutlined />, label: 'Agent receives message', accent: 'ink' },
        { icon: <ReadOutlined />, label: 'Walk AiAgentKnowledge', accent: 'ink' },
        { icon: <SearchOutlined />, label: 'Parallel hybrid search', accent: 'violet' },
        { icon: <ThunderboltOutlined />, label: 'Top-5 chunks injected', accent: 'green' },
      ]}
    />

    <Callout tone="info" title="Three-stage hybrid search">
      Vector (cosine) + BM25 (keyword) merged via Reciprocal Rank Fusion, then re-scored by a
      semantic reranker. RRF scores live in 0.01–0.05; the system filters at minimumScore = 0.01
      to drop noise. K = 3 per KB → top 5 overall.
    </Callout>

    <SectionTitle>Index field reference</SectionTitle>
    <KeyValueList
      items={[
        { key: 'id', value: 'Unique chunk identifier' },
        { key: 'content', value: 'Searchable text body (BM25 + vector)' },
        { key: 'contentVector', value: 'HNSW-indexed embedding (cosine)' },
        { key: 'MerchantId', value: 'Tenant isolation filter — every query scopes to the active merchant' },
        { key: 'KnowledgeId', value: 'KB filter — scopes search to one or more linked KBs' },
        { key: 'fileName / citationDisplayName', value: 'Surfaced in citations back to the user' },
        { key: 'pageNumber / chunkIndex', value: 'Anchors the chunk back to the source document' },
        { key: 'documentId', value: 'Groups all chunks from the same upload — proves the file was indexed' },
      ]}
    />

    <SectionTitle>Try it yourself</SectionTitle>
    <HelpParagraph>
      Open any KB and click the <b>Test query</b> tab — type any clinical question and you'll see
      exactly which chunks the agent would receive, ranked by relevance score, with the same
      hybrid + rerank pipeline used at chat time.
    </HelpParagraph>

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'agents.overview', label: 'Agents', icon: <RobotOutlined /> },
        { topic: 'prompts.system', label: 'System prompts', icon: <CodeOutlined /> },
        { topic: 'tools.plugins', label: 'AI plugins (tools)', icon: <ApiOutlined /> },
      ]}
    />
  </>
);

// ── Prebuilt Prompts ───────────────────────────────────────────────────────

const PrebuiltPromptsBody = () => (
  <>
    <HeroBlock
      icon={<ThunderboltOutlined />}
      title="Prebuilt prompts"
      tagline="Curated quick-action chips and operator pins. Authors create them here; the runtime surfaces them as one-click prompts above the chat box."
      accent="ink"
    />

    <HelpParagraph>
      A prebuilt prompt has two flavors. <b>QUICK_START</b> shows up as a chat chip — the user
      clicks and the prompt's <code>Instructions</code> get sent to the agent as if they typed it.{' '}
      <b>AGENT_INSTRUCTION</b> is a canned admin pin that operators can attach to an agent for
      reuse.
    </HelpParagraph>

    <SectionTitle>Where they show up</SectionTitle>
    <StepFlow
      steps={[
        {
          icon: <ThunderboltOutlined />,
          title: '1. Author the prompt here',
          body: 'Fill in description, button text, and the underlying Instructions. Pick the surface (Patient app / Care team).',
        },
        {
          icon: <PushpinOutlined />,
          title: '2. Pin to one or more agents',
          body: 'Use the "Pinned to N agents" column to attach this prompt to specific agents. The link table is ai.AssistantAIPrebuiltPrompt with DisplayOrder for left-to-right ordering.',
          accent: 'violet',
        },
        {
          icon: <RobotOutlined />,
          title: '3. Runtime — chat chip',
          body: 'When the user opens the agent\'s chat, the chips render above the input. Click → Instructions get sent to the agent as user input.',
          accent: 'green',
        },
      ]}
    />

    <SectionTitle>Schema</SectionTitle>
    <KeyValueList
      items={[
        { key: 'Type', value: 'QUICK_START (chat chip) | AGENT_INSTRUCTION (operator pin)' },
        { key: 'Description', value: 'Long-form copy used in the table + as fallback chip text' },
        { key: 'ButtonText', value: 'Short label users click in the chip' },
        { key: 'Instructions', value: 'The literal text sent to the agent on click' },
        { key: 'Help', value: 'Tooltip shown on chip hover' },
        { key: 'MemberApp / MerchantApp', value: 'Surface flags — patient view vs. care-team view' },
      ]}
    />

    <SectionTitle>Other surfaces</SectionTitle>
    <HelpParagraph>
      Prompts can also surface outside agent chat via two additional link tables:
    </HelpParagraph>
    <KeyValueList
      items={[
        {
          key: 'MerchantMenuPrebuiltPrompt',
          value: 'Pins a prompt into the merchant admin menu (joined to MerchantMenu2 by Path).',
        },
        {
          key: 'MemberRoutePrebuiltPrompt',
          value: 'Surfaces a prompt on a specific member-app route (matched by Path).',
        },
      ]}
    />

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'prompts.system', label: 'System prompts', icon: <CodeOutlined /> },
        { topic: 'agents.overview', label: 'Agents', icon: <RobotOutlined /> },
      ]}
    />
  </>
);

// ── System Prompts ─────────────────────────────────────────────────────────

const SystemPromptsBody = () => (
  <>
    <HeroBlock
      icon={<CodeOutlined />}
      title="System prompts"
      tagline="Reusable instruction blocks composed into the agent's system role via the capability chain."
      accent="violet"
    />

    <HelpParagraph>
      System prompts define an agent's <i>voice, reasoning style, and safety constraints</i>. They
      get wired in via four-tier composition — never directly to the agent — so the same prompt can
      serve many agents without duplication.
    </HelpParagraph>

    <SectionTitle>Composition chain</SectionTitle>
    <ChainDiagram
      nodes={[
        { icon: <RobotOutlined />, label: 'AiAgent', accent: 'ink' },
        { label: 'AiAgentCapability', accent: 'ink' },
        { label: 'AiCapability', accent: 'violet' },
        { label: 'SystemPromptAiCapability', accent: 'violet' },
        { icon: <CodeOutlined />, label: 'SystemPrompt', accent: 'green' },
      ]}
    />

    <HelpParagraph>
      At every chat turn, <code>AIAgentInstructionSyncService.BuildInstructions()</code> walks this
      chain and concatenates each matching prompt's <code>BaseInstructions</code> into a single
      system-role string for the LLM call.
    </HelpParagraph>

    <SectionTitle>Filtering rules</SectionTitle>
    <StepFlow
      steps={[
        {
          icon: <GlobalOutlined />,
          title: 'Globals first',
          body: 'Prompts whose UserTypes is empty (or contains the active user type) load before any capability-scoped prompts.',
        },
        {
          icon: <BranchesOutlined />,
          title: 'Per-capability prompts next',
          body: 'For each enabled capability on the agent, the loader pulls SystemPromptAiCapability rows with matching UserTypes filter.',
          accent: 'violet',
        },
        {
          icon: <ThunderboltOutlined />,
          title: 'Conditional load (optional)',
          body: 'When LoadFromFunctionEnabled = true, the runtime emits a directive to call SystemPromptPlugin.GetSystemPrompt(id, channel) at evaluation time instead of inlining BaseInstructions.',
          accent: 'amber',
        },
      ]}
    />

    <SectionTitle>Schema</SectionTitle>
    <KeyValueList
      items={[
        { key: 'Title', value: 'Author-facing label (e.g. "Triage assessment voice")' },
        { key: 'BaseInstructions', value: 'The text that gets injected into the system role' },
        { key: 'UserTypes', value: 'Who this prompt applies to: Patient, Provider, Caregiver, etc.' },
        { key: 'Tags', value: 'Library filtering / grouping (no runtime effect)' },
        { key: 'LoadFromFunctionEnabled', value: 'True = resolved by SystemPromptPlugin at runtime; false = inlined' },
      ]}
    />

    <Callout tone="warning" title="Tip">
      Don't reference specific tool names or function identifiers in the prompt body — those rot
      as the codebase evolves. Describe behavior and intent instead.
    </Callout>

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'prompts.prebuilt', label: 'Prebuilt prompts', icon: <ThunderboltOutlined /> },
        { topic: 'agents.overview', label: 'Agents', icon: <RobotOutlined /> },
        { topic: 'tools.plugins', label: 'AI plugins (tools)', icon: <ApiOutlined /> },
      ]}
    />
  </>
);

// ── Agents overview ────────────────────────────────────────────────────────

const AgentsOverviewBody = () => (
  <>
    <HeroBlock
      icon={<RobotOutlined />}
      title="Agents"
      tagline="An agent (ai.AssistantAI / AgentDefinition) bundles a persona, system prompts, retrieval, tools, skills, and peer consultation into a single runtime entity addressable by name."
      accent="ink"
    />

    <SectionTitle>Key fields and what they do</SectionTitle>
    <KeyValueList
      items={[
        { key: 'DisplayName / A2AAgentName', value: 'DisplayName is the UI label; A2AAgentName is the stable identifier code uses to look up the agent at runtime via BaseA2AAgent.LoadAgentConfig().' },
        { key: 'EnableSkillRouter', value: 'When true, SkillRoutingService pre-injects matching skills into the system prompt before the LLM call (saves tokens vs. advertising all skills).' },
        { key: 'EnableConsultation', value: 'Per-agent kill switch for A2A peer consultation. False = no peer tools injected.' },
        { key: 'MaxConsultationDepth', value: 'Recursion guard for peer chains. Hard-capped at AbsoluteMaxConsultationDepth = 5 regardless of DB value.' },
        { key: 'PatientApp / MerchantApp', value: 'Surface flags — controls which app rosters the agent appears in.' },
        { key: 'AIModelId', value: 'Selects the LLM model (gpt-4o, o1, etc.).' },
      ]}
    />

    <SectionTitle>A2A peer consultation</SectionTitle>
    <HelpParagraph>
      Any agent with <code>EnableConsultation = true</code> can route a sub-question to a peer
      agent via <code>InProcessA2AClient.SendAsync()</code> → <code>BaseA2AAgent.HandleMessageAsync()</code>.
      Three guards prevent runaway consultation:
    </HelpParagraph>
    <KeyValueList
      items={[
        { key: 'Depth counter', value: 'Incremented per hop; capped at AbsoluteMaxConsultationDepth = 5' },
        { key: 'Cycle detection', value: 'Peers already in the ConsultationChain are skipped' },
        { key: 'Per-agent gate', value: 'When ConsultationDepth >= MaxConsultationDepth, no peer tools are injected for that hop' },
      ]}
    />

    <SectionTitle>Skill router vs. on-demand load</SectionTitle>
    <StepFlow
      steps={[
        {
          icon: <ThunderboltOutlined />,
          title: 'Router on (recommended)',
          body: 'A small router LLM evaluates the user message and pre-injects only matching skill instructions into the system prompt. Saves tokens; reduces drift.',
          accent: 'green',
        },
        {
          icon: <ToolOutlined />,
          title: 'Router off',
          body: 'All skills are advertised in the system prompt. The main LLM calls load_skill(name) on demand. More flexible, more tokens.',
          accent: 'amber',
        },
      ]}
    />

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'procedures.skills', label: 'Skills', icon: <ToolOutlined /> },
        { topic: 'tools.plugins', label: 'AI plugins (tools)', icon: <ApiOutlined /> },
        { topic: 'knowledge.rag', label: 'Knowledge & RAG', icon: <ReadOutlined /> },
        { topic: 'prompts.system', label: 'System prompts', icon: <CodeOutlined /> },
      ]}
    />
  </>
);

// ── Procedures (Skills) ────────────────────────────────────────────────────

const ProceduresSkillsBody = () => (
  <>
    <HeroBlock
      icon={<ToolOutlined />}
      title="Skills"
      tagline="Reusable capabilities an agent loads on demand. Description sits in the system prompt; the full Instructions body only loads when needed — keeping baseline token usage low. (Built on Microsoft Agent Framework Skills under the hood.)"
      accent="violet"
    />

    <SectionTitle>Description vs. instructions</SectionTitle>
    <KeyValueList
      items={[
        { key: 'Name', value: 'Stable identifier (e.g. "oncology-conversational-triage")' },
        { key: 'Description (~100 tokens)', value: 'Always advertised in the agent\'s system prompt. The router LLM reads this to decide whether to load the skill.' },
        { key: 'Instructions (full body)', value: 'Only loaded when the router or the main LLM calls load_skill(name). This is the actual procedure.' },
        { key: 'Category', value: 'Clinical / Protocol / Operational / Communication — for library filtering' },
      ]}
    />

    <SectionTitle>The AlwaysLoad escape hatch</SectionTitle>
    <Callout tone="info">
      <code>AiAgentSkill.AlwaysLoad = true</code> + <code>AgentDefinition.EnableSkillRouter = true</code> →
      the skill bypasses the router and is <b>always</b> loaded. Use this for cross-cutting skills
      (e.g. conversational triage) that smaller router models tend to skip.
    </Callout>

    <SectionTitle>Runtime path</SectionTitle>
    <ChainDiagram
      nodes={[
        { icon: <RobotOutlined />, label: 'Agent starts', accent: 'ink' },
        { label: 'DatabaseSkillsProviderFactory.CreateForAgent(agentId)', accent: 'ink' },
        { label: 'Query AiSkill ⨝ AiAgentSkill', accent: 'violet' },
        { label: 'AgentInlineSkill[]', accent: 'violet' },
        { label: 'AgentSkillsProvider injected', accent: 'green' },
      ]}
    />

    <HelpParagraph>
      The factory wires <code>AgentSkillsProvider</code> (a Microsoft Agent Framework{' '}
      <code>AIContextProvider</code>) into the agent's pipeline. Skills are discovered at startup;
      the agent calls <code>load_skill(name)</code> when it needs one.
    </HelpParagraph>

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'agents.overview', label: 'Agents', icon: <RobotOutlined /> },
        { topic: 'tools.plugins', label: 'AI plugins (tools)', icon: <ApiOutlined /> },
      ]}
    />
  </>
);

// ── Tools / AI Plugins ─────────────────────────────────────────────────────

const ToolsPluginsBody = () => (
  <>
    <HeroBlock
      icon={<ApiOutlined />}
      title="AI plugins (tools)"
      tagline="C# classes the agent can call as tools. Reflection over AIPluginToolProvider.Classes turns each public method into an AIOptionTool the LLM sees in its tool catalog."
      accent="amber"
    />

    <SectionTitle>The provisioning chain</SectionTitle>
    <ChainDiagram
      nodes={[
        { label: 'AIPluginToolProvider.Classes', accent: 'ink' },
        { label: 'PluginClassInfo', accent: 'ink' },
        { label: 'Reflection → methods', accent: 'violet' },
        { label: 'AIOptionTool', accent: 'violet' },
        { label: 'LLM tool call', accent: 'green' },
      ]}
    />

    <HelpParagraph>
      A C# class becomes a tool when (a) it's listed in <code>AIPluginToolProvider.Classes</code>{' '}
      <i>and</i> (b) there's a row in <code>ai.AiPlugin</code> +{' '}
      <code>ai.AiCapabilityPlugin</code> wiring it to a capability the agent has enabled.
      Compile-time + DB-time both gate visibility.
    </HelpParagraph>

    <SectionTitle>Security model — IMemberCareAgentContext</SectionTitle>
    <Callout tone="warning" title="Never accept member/merchant ids from the LLM">
      Tools never declare <code>memberId</code> or <code>merchantId</code> as LLM-visible
      parameters. Instead, every plugin receives <code>IMemberCareAgentContext</code> via DI,
      populated from the JWT claims. Methods like <code>RequireMemberId()</code> fail fast if the
      context is missing — preventing hallucinated IDs from ever reaching the database.
    </Callout>

    <SectionTitle>Three patterns to know</SectionTitle>
    <StepFlow
      steps={[
        {
          icon: <WarningOutlined />,
          title: 'max_output_tokens overflow',
          body: 'Never accept raw data arrays as tool args — the LLM has to write them back into output tokens, hitting the 32K limit mid-JSON. Use the fused-tool pattern instead.',
          accent: 'amber',
        },
        {
          icon: <SafetyOutlined />,
          title: 'Fused-tool pattern',
          body: 'One method that fetches data AND renders the chart/table in a single call. Tool schema accepts only clinical query params, never raw data.',
          accent: 'violet',
        },
        {
          icon: <BranchesOutlined />,
          title: 'Manager-based provisioning',
          body: 'Plugins read from IMemberCareAgentContext, never from LLM arguments. Tools are provisioned via AIPluginToolProvider.SetTools() at agent build time.',
        },
      ]}
    />

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'agents.overview', label: 'Agents', icon: <RobotOutlined /> },
        { topic: 'procedures.skills', label: 'Skills', icon: <ToolOutlined /> },
      ]}
    />
  </>
);

// ── Validations / Recent Runs / Scenarios ──────────────────────────────────

const ValidationsBody = () => (
  <>
    <HeroBlock
      icon={<CheckSquareOutlined />}
      title="Agent validations"
      tagline="Multi-turn scenario testing of every agent. Eleven tables in the aivalidation.* schema track every run, turn, tool call, router decision, and assertion outcome."
      accent="green"
    />

    <SectionTitle>The data model</SectionTitle>
    <ChainDiagram
      nodes={[
        { icon: <PlayCircleOutlined />, label: 'ValidationRun', accent: 'ink' },
        { label: 'ValidationRunResult', accent: 'ink' },
        { label: 'ValidationRunResultTurn', accent: 'violet' },
        { label: 'Router + ToolInvocation + AssertionResult', accent: 'violet' },
      ]}
    />

    <HelpParagraph>
      A run is a top-level <code>ValidationRun</code> row. Each scenario in the suite produces a{' '}
      <code>ValidationRunResult</code>, which contains <code>ValidationRunResultTurn</code> rows,
      each with their own router decisions, tool invocations, and turn-level assertion results.
      Conversation-level assertions (e.g. "must mention dose-hold criteria at least once") roll up
      to <code>ValidationRunConversationAssertionResult</code>.
    </HelpParagraph>

    <SectionTitle>Anatomy of a scenario</SectionTitle>
    <CodeBlock
      language="json"
      code={`{
  "scenarioId": "folfox-patient-severe-nausea-day4",
  "category": "K",                 // Progressive disclosure
  "turns": [
    {
      "user": "I am experiencing nausea",
      "expect": {
        "skillsLoaded": ["oncology-conversational-triage"]
      }
    },
    { "user": "4", ... }
  ],
  "conversationLevelAssertions": [
    { "rule": "mustAppearAtLeastOnce", "phrase": "Day 4" }
  ]
}`}
    />

    <SectionTitle>What pass/fail means</SectionTitle>
    <StepFlow
      steps={[
        {
          icon: <CheckSquareOutlined />,
          title: 'Per-turn assertions',
          body: 'expect.skillsLoaded, responseMustContainAnyOf, responseMustNotContain. Fail rolls up into ValidationRunAssertionFailure with a CheckName.',
          accent: 'green',
        },
        {
          icon: <ExperimentOutlined />,
          title: 'Conversation-level rules',
          body: 'mustAppearAtLeastOnce / mustNotAppearInAnyTurn — applied across all turns in the scenario.',
          accent: 'violet',
        },
        {
          icon: <WarningOutlined />,
          title: 'Number-grounding (Layer 3)',
          body: 'When a turn has RequireNumbersGroundedInToolOutput = true, the framework verifies every number in the agent\'s reply traces back to a tool result, with optional GroundingTolerance + GroundingIgnorePatterns.',
          accent: 'amber',
        },
      ]}
    />

    <Callout tone="info" title="CheckName is open-ended">
      Treat <code>CheckName</code>, <code>RuleName</code>, and <code>Status</code> as open enums
      sourced from data, not closed enums hardcoded in the frontend. New failure modes (e.g.{' '}
      <code>number-not-grounded-in-tool-output</code>) get added as new defenses ship.
    </Callout>

    <SectionTitle>Where to look in the UI</SectionTitle>
    <KeyValueList
      items={[
        { key: 'Recent runs', value: 'Live feed of every ValidationRun across the merchant' },
        { key: 'Scenario library', value: 'Catalog of scenarios with run history per scenario' },
        { key: 'Health Checks', value: 'Per-agent overview — coverage matrix, retention targets' },
      ]}
    />

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'agents.overview', label: 'Agents', icon: <RobotOutlined /> },
        { topic: 'procedures.skills', label: 'Skills', icon: <ToolOutlined /> },
      ]}
    />
  </>
);

// ── Validations — page-level help (layman language) ──────────────────────
//
// Each of these page-level topics is written for a clinical SME, an analyst,
// or a non-engineer program manager. Tone: "this is what the screen does and
// here's what to click." Engineering details (ValidationRunResultTurn schema,
// router-decision linkage, etc.) live in the technical 'validations.overview'
// topic above and are intentionally NOT repeated here. If a future reader
// thinks "this is too basic," they're not the audience — keep it that way.

const ScenarioLibraryHelpBody = () => (
  <>
    <HeroBlock
      icon={<ExperimentOutlined />}
      title="Scenario library"
      tagline="A scenario is a scripted conversation we replay against an AI agent to make sure it still behaves correctly. This page lists every scenario we have on file."
      accent="violet"
    />

    <SectionTitle>What you can do here</SectionTitle>
    <StepFlow
      steps={[
        {
          icon: <RobotOutlined />,
          title: 'Pick the agent you care about',
          body: 'Use the Agent picker to narrow the list to one agent (e.g. the FOLFOX patient agent). Each agent has its own set of scenarios.',
          accent: 'ink',
        },
        {
          icon: <FilterOutlined />,
          title: 'Filter by what matters',
          body: 'Single-turn vs multi-turn, audience (Patient / Provider / …), category (safety, symptom triage, adversarial, …), or tags. Filters stack — combine as many as you like.',
          accent: 'violet',
        },
        {
          icon: <SearchOutlined />,
          title: 'Open one to see the conversation',
          body: 'Click any row to open the scenario detail. You will see the user prompt(s), the rules we expect the agent to follow, and the history of every time it ran.',
          accent: 'green',
        },
      ]}
    />

    <SectionTitle>If the list looks empty</SectionTitle>
    <KeyValueList
      items={[
        {
          key: 'You picked an agent that has no scenarios yet',
          value: 'Try “All agents” to see what is available. Authoring scenarios is a separate workflow.',
        },
        {
          key: 'You have filters turned on',
          value: 'Click “Clear filters” at the right side of the filter bar to reset.',
        },
        {
          key: 'A new scenario you authored is not showing',
          value: 'New JSON files are picked up on the next CI sync. Click Refresh; if it still does not appear, ask an engineer to run the sync provisioning test.',
        },
      ]}
    />

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'validations.scenarios.detail', label: 'Scenario detail', icon: <MessageOutlined /> },
        { topic: 'validations.runs.recent', label: 'Recent runs', icon: <HistoryOutlined /> },
        { topic: 'validations.overview', label: 'How validations work (technical)', icon: <CheckSquareOutlined /> },
      ]}
    />
  </>
);

const ScenarioFilterHelpBody = () => (
  <>
    <HeroBlock
      icon={<FilterOutlined />}
      title="Filtering the scenario library"
      tagline="Use filters to narrow a long list down to the scenarios that match what you are working on right now."
      accent="violet"
    />

    <SectionTitle>What each filter means</SectionTitle>
    <KeyValueList
      items={[
        { key: 'Agent', value: 'Show only scenarios for one agent. Most of the time you want this set.' },
        { key: 'Audience', value: 'Who the agent talks to — Patient, Provider, Customer, Internal, … Each agent is configured with one audience type.' },
        { key: 'Single-turn vs multi-turn', value: 'Single-turn = one prompt, one reply. Multi-turn = a conversation with at least two back-and-forth exchanges. Multi-turn is where context, memory, and follow-up matter.' },
        { key: 'Category', value: 'A short label for what the scenario is about — safety, symptom triage, adversarial, etc. Categories are configured per vertical (oncology, RPM, …) so they fit the domain you are in.' },
        { key: 'Has banned phrases', value: 'Scenarios that explicitly forbid certain wording in the agent reply (e.g. specific drug names). Useful when chasing a wording regression.' },
        { key: 'Has skill assertions', value: 'Scenarios that pin which skills the agent must (or must not) use. These catch bugs in the skill router.' },
        { key: 'Tags', value: 'Free-form labels authors can attach to a scenario. Type any word; we match anything containing it.' },
      ]}
    />

    <Callout tone="info" title="Filters add up — they don't replace each other">
      Picking “Patient + Multi-turn + Category: Safety” shows scenarios that match all three. There is no “OR” mode; if the result feels too narrow, remove a filter.
    </Callout>

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'validations.scenarios.library', label: 'Scenario library', icon: <ExperimentOutlined /> },
      ]}
    />
  </>
);

const ScenarioDetailHelpBody = () => (
  <>
    <HeroBlock
      icon={<MessageOutlined />}
      title="Scenario detail"
      tagline="Everything we expect to see in this conversation, plus the history of how it has performed."
      accent="violet"
    />

    <SectionTitle>What you are looking at</SectionTitle>
    <KeyValueList
      items={[
        { key: 'Conversation', value: 'The user prompt(s) we send and the rules we expect the agent reply to follow. For multi-turn scenarios, you see every back-and-forth turn.' },
        { key: 'Per-turn rules', value: 'Things like “the reply must mention X”, “must not mention Y”, “must use the symptom-triage skill”. Each rule produces a pass/fail when we run the scenario.' },
        { key: 'Conversation-level rules', value: 'Rules that span the whole conversation — e.g. “a specific drug name must NEVER appear in any reply.” These are typically the safety-critical ones.' },
        { key: 'Run history', value: 'Every time we have replayed this scenario against the agent — newest first. Green = all rules passed. Red = at least one rule failed; click in to see which.' },
      ]}
    />

    <SectionTitle>If the scenario looks wrong</SectionTitle>
    <HelpParagraph>
      Scenarios are authored as JSON files in source control — this screen is read-only. To change what the
      scenario asks or what it expects, edit the JSON file (or use the upcoming authoring flow when it ships)
      and merge a pull request. The next sync picks up the change.
    </HelpParagraph>

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'validations.scenarios.library', label: 'Scenario library', icon: <ExperimentOutlined /> },
        { topic: 'validations.runs.detail', label: 'Run detail', icon: <PlayCircleOutlined /> },
      ]}
    />
  </>
);

const RecentRunsHelpBody = () => (
  <>
    <HeroBlock
      icon={<HistoryOutlined />}
      title="Recent runs"
      tagline="A live feed of every batch of scenarios we have replayed against the agents — newest first."
      accent="violet"
    />

    <SectionTitle>How to read a row</SectionTitle>
    <KeyValueList
      items={[
        { key: 'Agent', value: 'Which agent the batch was run against.' },
        { key: 'Suite', value: 'The named bundle of scenarios — usually one suite per agent (e.g. “FOLFOX Patient — Alpha Suite”).' },
        { key: 'Scenarios', value: 'Total scenarios in this run, then how many passed and how many failed.' },
        { key: 'Run date', value: 'When the run finished (your local time zone).' },
        { key: 'Trigger', value: '“manual” = engineer kicked it off. “ci” = automated pipeline. Other values are merchant-specific.' },
      ]}
    />

    <SectionTitle>What to do next</SectionTitle>
    <StepFlow
      steps={[
        {
          icon: <SearchOutlined />,
          title: 'Spot a red row?',
          body: 'Click into the run to see exactly which scenarios failed and why. You can copy a clean reproduction step from there.',
          accent: 'amber',
        },
        {
          icon: <ApartmentOutlined />,
          title: 'Want the HTML report?',
          body: 'Each run has an HTML report attached — full chat-bubble transcripts of every conversation. Open it from the run detail page.',
          accent: 'ink',
        },
      ]}
    />

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'validations.runs.detail', label: 'Run detail', icon: <PlayCircleOutlined /> },
        { topic: 'validations.scenarios.library', label: 'Scenario library', icon: <ExperimentOutlined /> },
      ]}
    />
  </>
);

const RunDetailHelpBody = () => (
  <>
    <HeroBlock
      icon={<PlayCircleOutlined />}
      title="Run detail"
      tagline="Everything that happened during one batch run — every scenario, every turn, every rule check."
      accent="violet"
    />

    <SectionTitle>What you can do here</SectionTitle>
    <KeyValueList
      items={[
        { key: 'See per-scenario outcomes', value: 'Each scenario in the suite gets a row — green pass, red fail, grey errored. Failed scenarios link straight to the failing rule.' },
        { key: 'Open the HTML report', value: 'A formatted, chat-bubble transcript of every conversation in the suite. Useful for sharing with non-engineers.' },
        { key: 'Compare against earlier runs', value: 'The run is one snapshot. To see whether things are improving or getting worse, look at Recent Runs and trace the agent over time.' },
      ]}
    />

    <SectionTitle>About flaky vs broken</SectionTitle>
    <Callout tone="warning" title="Don't panic on a single failure">
      AI replies have small natural variance. One failure across 20 runs may be flake. The same scenario failing 3 runs in a row is the signal you investigate. The “Recent runs” page makes the pattern visible.
    </Callout>

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'validations.runs.recent', label: 'Recent runs', icon: <HistoryOutlined /> },
        { topic: 'validations.scenarios.detail', label: 'Scenario detail', icon: <MessageOutlined /> },
      ]}
    />
  </>
);

// ── Costs ──────────────────────────────────────────────────────────────────

// Costs guides live beside the page: features/costs/costsHelp.tsx.

// ── Models ─────────────────────────────────────────────────────────────────

const ModelsBody = () => (
  <>
    <HeroBlock
      icon={<CodeOutlined />}
      title="Models"
      tagline="Catalog of LLM and embedding models the platform routes traffic to. Every agent picks one model id; admins can swap models centrally."
      accent="ink"
    />
    <HelpParagraph>
      Each agent's <code>AIModelId</code> field points to a row in the models registry. Embedding
      and generation models for RAG live in <code>ApplicationSettings</code> under the keys{' '}
      <code>TextEmbeddingGenerationModel</code> and <code>TextGenerationModel</code> — visible
      under any KB's <b>Settings</b> tab.
    </HelpParagraph>
    <Callout tone="warning" title="Embedding model changes are destructive">
      Switching the embedding model invalidates the index. Re-embedding every existing chunk is
      required — the platform doesn't do this automatically.
    </Callout>
    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'knowledge.rag', label: 'Knowledge & RAG', icon: <ReadOutlined /> },
        { topic: 'costs.overview', label: 'Costs', icon: <AppstoreOutlined /> },
      ]}
    />
  </>
);

// ── Merchants ──────────────────────────────────────────────────────────────

const MerchantsBody = () => (
  <>
    <HeroBlock
      icon={<GlobalOutlined />}
      title="Merchants"
      tagline="Multi-tenancy primitive. Every agent, KB, prompt, plugin link, and validation run is scoped to a merchant via MerchantId."
      accent="violet"
    />
    <HelpParagraph>
      The merchant switcher in the top bar drives the entire scoped view. Selecting a different
      merchant refetches agents, KBs, prompts, validations — anything keyed by{' '}
      <code>MerchantId</code> reacts via MobX <code>reaction()</code>.
    </HelpParagraph>
    <SectionTitle>What's merchant-scoped</SectionTitle>
    <KeyValueList
      items={[
        { key: 'ai.AssistantAI', value: 'Agents — both via AssistantAI.MerchantId and AiAgentMerchant link table' },
        { key: 'ai.Knowledge', value: 'Knowledge bases — MerchantId column' },
        { key: 'ai.PrebuiltPrompt', value: 'Prompts — surfaced through MerchantMenu link table' },
        { key: 'ai.AiPlugin', value: 'Plugin enablement (the C# classes are global; the wiring is per-merchant)' },
        { key: 'aivalidation.ValidationRun', value: 'Validation runs and scenarios' },
      ]}
    />
    <SectionTitle>What's NOT merchant-scoped</SectionTitle>
    <KeyValueList
      items={[
        { key: 'Azure AI Search index', value: 'Single shared index "enhanced-v1" — multi-tenancy enforced via MerchantId filter on every query.' },
        { key: 'ai.AiSkill', value: 'Skills are global by name today — no MerchantId column.' },
        { key: 'ai.SystemPrompt', value: 'System prompts are global; merchant scoping happens via the capability chain.' },
      ]}
    />
  </>
);

// ── Audit log ──────────────────────────────────────────────────────────────

const AuditBody = () => (
  <>
    <HeroBlock
      icon={<SafetyOutlined />}
      title="Audit log"
      tagline="Cross-merchant ledger of every administrative action. Every write to a sensitive table emits a row with actor, timestamp, before/after, and the originating request id."
      accent="amber"
    />
    <HelpParagraph>
      Audit rows are append-only — no UI lets you delete them. They're keyed by{' '}
      <code>RequestId</code> so you can trace one user action across every downstream write.
    </HelpParagraph>
  </>
);

// ── Insights overview ─────────────────────────────────────────────────────

const InsightsOverviewBody = () => (
  <>
    <HeroBlock
      icon={<AreaChartOutlined />}
      title="Insights"
      tagline="Seven cross-table analytics derived from the platform's AI log tables. Each query is a single GROUP BY against a column denormalized for that purpose — no joins beyond the merchant scope filter."
      accent="violet"
    />

    <HelpParagraph>
      The platform writes structured rows on every LLM call, every tool invocation, every A2A
      consultation, and every validation turn. The Insights page surfaces this telemetry as
      seven aggregated views — answering questions an engineer would otherwise hand-write SQL
      for.
    </HelpParagraph>

    <SectionTitle>The seven insights</SectionTitle>
    <KeyValueList
      items={[
        {
          key: '1. Token cost',
          value: 'Sum of EstimatedCostUsd from PromptLog grouped by (agent × model). Catches the "which agent burns the most tokens?" question in one glance.',
        },
        {
          key: '2. Context overload',
          value: 'PromptLog.PercentOfContext rolled up to Critical / Warning / Healthy counts. Critical = prompt used >= 95% of the model\'s MaxContextTokens.',
        },
        {
          key: '3. Compaction effectiveness',
          value: 'When ContextCompactor fires, average token reduction per strategy. Lets you tune SlidingWindow vs Summarize.',
        },
        {
          key: '4. Skill router accuracy',
          value: 'Daily Routed vs Failed-fallback counts from ValidationRunRouterDecision. The trend shows whether the router is improving over time.',
        },
        {
          key: '5. Safety flakiness',
          value: 'Per-scenario pass rate across runs for IsSafety=true scenarios. Sorted lowest-pass-rate first — first to look at when triaging failures.',
        },
        {
          key: '6. Tool overflow risk',
          value: 'Top 20 plugin methods by max ArgumentCharCount (the leading indicator for output-token overflow that caused the 2026-04-23 incident).',
        },
        {
          key: '7. A2A consultation latency',
          value: 'Median + P95 + max consultation time per (primary, peer) agent pair. Identifies slow consultees that need investigation.',
        },
      ]}
    />

    <SectionTitle>Source tables</SectionTitle>
    <ChainDiagram
      nodes={[
        { icon: <DollarCircleOutlined />, label: 'PromptLog', accent: 'amber' },
        { icon: <CompressOutlined />, label: 'ToolCallingLog', accent: 'violet' },
        { icon: <RobotOutlined />, label: 'ConsultationLog', accent: 'ink' },
        { icon: <ApartmentOutlined />, label: 'ValidationRun*', accent: 'green' },
      ]}
    />

    <Callout tone="info" title="All endpoints are merchant-scoped">
      Every <code>/api/Insights/*</code> endpoint takes <code>merchantId</code> from the JWT —
      clients never pass it. Validation tables (which don't carry MerchantId) join through
      <code> ai.AssistantAI.MerchantId</code> for isolation.
    </Callout>

    <SectionTitle>Related</SectionTitle>
    <RelatedTopics
      items={[
        { topic: 'costs.overview', label: 'Costs', icon: <DollarCircleOutlined /> },
        { topic: 'validations.overview', label: 'Validations', icon: <ApartmentOutlined /> },
        { topic: 'tools.plugins', label: 'AI plugins (tools)', icon: <ApiOutlined /> },
        { topic: 'agents.overview', label: 'Agents', icon: <RobotOutlined /> },
      ]}
    />
  </>
);

// ── Discoverable abilities (A2A skills advertised to peers) ────────────────

const DiscoverableAbilitiesBody = () => (
  <>
    <HeroBlock
      icon={<GlobalOutlined />}
      title="Discoverable abilities"
      tagline="Stored in ai.AiA2AAgentSkill — these are the things this agent advertises to OTHER agents during peer consultation. Distinct from Skills (which run on this agent's own turns)."
      accent="violet"
    />

    <SectionTitle>How they're used at runtime</SectionTitle>
    <HelpParagraph>
      When a peer agent decides to consult this agent, the A2A client builds a tool list
      from these abilities and exposes them on the consulting agent's LLM call. Only
      agents with <code>EnableConsultation = true</code> can be consulted.
    </HelpParagraph>

    <SectionTitle>Authoring an ability</SectionTitle>
    <KeyValueList
      items={[
        { key: 'Name', value: 'Stable verb-phrase identifier — peer agents see it as a tool name (e.g. "lookup_glucose_history").' },
        { key: 'Description', value: 'Always sent to the consulting agent. The peer LLM reads this to decide whether to call.' },
        { key: 'Inputs', value: 'JSON schema shape advertised on the tool — keep it small.' },
      ]}
    />

    <RelatedTopics
      items={[
        { topic: 'agents.overview', label: 'Agents', icon: <RobotOutlined /> },
        { topic: 'procedures.skills', label: 'Skills (own-turn capabilities)', icon: <ToolOutlined /> },
      ]}
    />
  </>
);

// ── Audience (PatientApp / MerchantApp) ────────────────────────────────────

const AudienceBody = () => (
  <>
    <HeroBlock
      icon={<RobotOutlined />}
      title="Audience"
      tagline="Two flags on AgentDefinition — PatientApp and MerchantApp — control which surfaces this agent appears on. Both, either, or neither are valid."
      accent="green"
    />

    <SectionTitle>What each surface means</SectionTitle>
    <KeyValueList
      items={[
        { key: 'Patient', value: 'PatientApp = true. Agent shows up in the patient-facing app roster. The agent code typically pivots tone, redaction, and tool surface for this audience.' },
        { key: 'Care team', value: 'MerchantApp = true. Agent shows up in the clinician/care-team console. Often where richer tool calls (write-back, notify, assign) are exposed.' },
        { key: 'Internal only', value: 'Both flags false. Agent exists, but no app surfaces it — used for peer-only agents that are consulted by other agents but never chatted with directly.' },
      ]}
    />

    <Callout tone="info">
      Audience isn't just a filter for the roster — the agent code can branch on the calling
      surface (Patient vs. Care team) inside its tools, so flipping these flags also affects
      runtime behavior, not just visibility.
    </Callout>
  </>
);

// ── Skill router (per-agent toggle) ────────────────────────────────────────

const SkillRouterBody = () => (
  <>
    <HeroBlock
      icon={<ThunderboltOutlined />}
      title="Skill router"
      tagline="The router is a small model-agnostic LLM call that runs BEFORE the main turn and pre-selects which of this agent's skills to inject into the system prompt."
      accent="ink"
    />

    <SectionTitle>What changes when you flip it</SectionTitle>
    <StepFlow
      steps={[
        {
          icon: <ThunderboltOutlined />,
          title: 'Router enabled',
          body: 'Per turn: SkillRoutingService picks matching skills, their full instructions are added to the system prompt, the main LLM only sees what it needs. Token cost scales with skill count, not full library.',
          accent: 'green',
        },
        {
          icon: <ToolOutlined />,
          title: 'Router disabled',
          body: 'Every skill\'s description is advertised on every turn; the main LLM may call load_skill on its own. Easier to reason about, more tokens per turn, cost grows with skill library size.',
          accent: 'amber',
        },
      ]}
    />

    <SectionTitle>When to keep it off</SectionTitle>
    <HelpParagraph>
      For agents with very few skills (≤ 3), or where you want the main LLM to see all
      tool surfaces for each turn, the router adds latency without much benefit. Above
      ~5 skills, the router pays for itself in tokens saved.
    </HelpParagraph>

    <RelatedTopics
      items={[
        { topic: 'procedures.skills', label: 'Skills', icon: <ToolOutlined /> },
        { topic: 'agents.overview', label: 'Agents', icon: <RobotOutlined /> },
      ]}
    />
  </>
);

// ── Workflows (ai.AIProcessLibrary) ────────────────────────────────────────

const WorkflowsBody = () => (
  <>
    <HeroBlock
      icon={<ApartmentOutlined />}
      title="Workflows"
      tagline="Stored in ai.AIProcessLibrary — each row is a multi-step orchestration. The Workflow JSON column holds a list of nodes, each pinned to an executor like EXECUTE_AI_PROMPT, AGENT_NODE_EXECUTOR, AI_GENERATE_PDF, NOTIFY_CARE_TEAM. Most are linear pipelines; some branch on conditions."
      accent="violet"
    />

    <SectionTitle>What a workflow is for</SectionTitle>
    <HelpParagraph>
      Where an <em>agent</em> handles a conversational turn, a <em>workflow</em> handles a
      structured business process — generate a clinical summary, run a HEDIS quality check,
      route a lab result to a specialist agent and notify the care team. Workflows can pause
      and ask the user for input, retry with backoff, and persist state so they can resume
      after a crash.
    </HelpParagraph>

    <SectionTitle>Anatomy of a node</SectionTitle>
    <KeyValueList
      items={[
        { key: 'ClassId', value: 'Logical executor key (string). Maps to a C# executor at runtime via AIProcessManager. Examples: EXECUTE_AI_PROMPT, AGENT_NODE_EXECUTOR, STORE_SQL_OBJ, NOTIFY_CARE_TEAM, AI_GENERATE_PDF.' },
        { key: 'UseAgent', value: 'When set, references a workflow-local agent definition (in the Agents array) — that agent runs the node with its own instructions and tools.' },
        { key: 'Arguments', value: 'Executor-specific config — Prompt body, PromptName, Plugins enabled for this step, Log message displayed to operators while it runs.' },
        { key: 'NodesConnected', value: 'Edges to next nodes. Empty = end of branch. >1 = fan-out.' },
        { key: 'ConditionalEdges', value: 'When present, the runtime evaluates each edge\'s condition against the previous step\'s output and routes accordingly.' },
        { key: 'Retry / Timeout / CircuitBreaker', value: 'Per-node resilience policies — exponential backoff, hard timeout, fail-fast after N consecutive errors.' },
        { key: 'Interaction', value: 'When Required = true, the workflow pauses, persists state, and waits for the user to click a button or submit text before resuming.' },
      ]}
    />

    <SectionTitle>Library vs. instance</SectionTitle>
    <HelpParagraph>
      <code>ai.AIProcessLibrary</code> holds the <em>template</em> (this is what Agent Studio
      browses). When the runtime kicks off a workflow, it spawns an <code>AIProcess</code>
      record in <code>usr.UserProcess</code> — a live instance with status, logs, checkpoints,
      and pending interactions. The instance UI lands in a follow-up.
    </HelpParagraph>

    <Callout tone="info">
      Workflows can fan out to AI calls, PDF generation, care-team notifications, and SQL/Cosmos
      writes. The "Run" surface in Agent Studio is intentionally not exposed yet — it needs
      careful per-merchant scoping and a dry-run preview before it's safe to expose to operators.
    </Callout>
  </>
);

// ── Grounding signal (added 2026-05-03 from the silent-skill-loss diagnosis) ──

const GroundingSkillInjectionBody = () => (
  <>
    <HeroBlock
      icon={<ThunderboltOutlined />}
      title="Did skills/KB actually reach the LLM?"
      tagline="The grounding panel on every LLM event tells you — at a glance — whether the system prompt got skill descriptors and KB content layered on top of the agent's bare instructions, or whether the agent reached the model with nothing but its static instructions text."
      accent="violet"
    />

    <HelpParagraph>
      Before May 2026 this question was un-answerable from existing telemetry. <code>InputTokenCount</code>{' '}
      could be large because of long tool returns (skill bodies loaded via <code>load_skill</code>),
      not because skills hit the system prompt itself. We discovered that an agent could reach the
      LLM with nothing but its static <code>ai.AiAgent.Instructions</code> — six months of silent
      under-grounding before the cascade was traced end-to-end. The grounding panel makes the
      distinction explicit.
    </HelpParagraph>

    <SectionTitle>How to read the badge</SectionTitle>
    <KeyValueList
      items={[
        {
          key: 'Grounded · +N tok',
          value: 'Green. The system prompt grew by N tokens above the bare instructions — skill descriptors, KB context, or both have been injected by AIContextProviders. This is the healthy state.',
        },
        {
          key: 'BARE · system ≈ base',
          value: 'Red. The system prompt is roughly the same size as the agent\'s static instructions — meaning AgentSkillsProvider didn\'t register or context-providers were silently skipped. This is the silent-skill-loss bug pattern. Investigate immediately.',
        },
        {
          key: 'Partial · +N tok',
          value: 'Slate. Some grounding landed but not enough to be confident — usually means a single small skill descriptor or KB hit. May be intentional; check the per-skill chips below the badge.',
        },
        {
          key: '—',
          value: 'Grey. The row predates SystemPromptTokens instrumentation (before 2026-05-03) — distinct from a measurement of zero, which would be its own signal.',
        },
      ]}
    />

    <SectionTitle>The token math (why chars/4)</SectionTitle>
    <HelpParagraph>
      Token counts use a chars/4 estimate, the same heuristic <code>AICompletionService.SetOptionsLog</code>{' '}
      uses for <code>SystemPromptTokens</code>. It's within ~5% of the real BPE encoding for English
      text and we don't need exactness — we need order-of-magnitude (3K vs 8K). Per-skill descriptor
      footprint is held at 100 tokens each, matching the MAF <code>AgentSkillsProvider</code>{' '}
      documented per-skill cost.
    </HelpParagraph>

    <SectionTitle>Backend dashboard query</SectionTitle>
    <CodeBlock
      language="sql"
      code={`-- Per-agent grounding health, last 24h
-- Low values = bare instructions = under-grounded
SELECT TOP 100
       AgentId,
       AVG(SystemPromptTokens) AS AvgSystemTokens,
       COUNT(*)                AS Calls
FROM   ai.PromptLog
WHERE  adt_CreatedDate > DATEADD(DAY, -1, GETUTCDATE())
  AND  SystemPromptTokens IS NOT NULL
GROUP  BY AgentId
ORDER  BY AvgSystemTokens ASC;
-- Compare against ai.AiAgent.Instructions length / 4 to see grounded delta`}
    />

    <Callout tone="warning" title="Watch for these patterns">
      An agent whose grounded delta is consistently &lt; 200 tok despite having skills linked in{' '}
      <code>ai.AiAgentSkill</code> is silently under-grounded. The fix landed via{' '}
      <code>IDatabaseSkillsProviderFactory</code> + the self-bootstrap in{' '}
      <code>AIWorkflowService</code> ctor; if you see this on a NEW path, that path bypassed DI
      somewhere new. Check App Insights for{' '}
      <code>[DatabaseSkillsProviderFactory] Created AgentSkillsProvider</code> — the trace tells
      you whether the runtime registered the provider at all.
    </Callout>

    <RelatedTopics
      items={[
        { topic: 'grounding.load-sources', label: 'LoadSource labels' },
        { topic: 'grounding.silent-skill-loss', label: 'Silent skill-loss bug' },
      ]}
    />
  </>
);

const GroundingLoadSourcesBody = () => (
  <>
    <HeroBlock
      icon={<BranchesOutlined />}
      title="LoadSource labels — what each chip means"
      tagline="Every loaded skill gets a colored chip showing how it ended up in scope. The label is the canonical PromptLogSkillLoad.LoadSources value and tells you precisely how the runtime decided this skill belonged on this turn."
      accent="ink"
    />

    <KeyValueList
      items={[
        {
          key: 'AlwaysLoad (green)',
          value: 'ai.AiAgentSkill.AlwaysLoad = 1 — a cross-cutting skill that bypasses any router. Common pattern: an output-formatter or final-envelope synthesizer that every turn must use. Intentional.',
        },
        {
          key: 'AgentLink (blue)',
          value: 'A generic ai.AiAgentSkill link. The skill is wired to the agent in the DB and gets eager-loaded on every turn that doesn\'t involve a router. Default for most workflow agents.',
        },
        {
          key: 'RouterPicked (violet)',
          value: 'SkillRoutingService picked this skill for THIS specific turn based on the user\'s message. Highest fidelity — the router believed this skill was needed here.',
        },
        {
          key: 'Inline (grey)',
          value: 'Hardcoded into the agent\'s Instructions body. Legacy pattern from before the skill system existed. No runtime injection — the content lives in the instructions text itself.',
        },
        {
          key: 'Advertised',
          value: 'The agent has its skill router switched off, so every linked skill was offered to the model as a short descriptor. A skill body is loaded only when the model asks for it. Expected for agents that run without the router; added 31 October 2026 with the routing status on each prompt log.',
        },
        {
          key: 'AuditFallback (red, outlined)',
          value: 'INVESTIGATE. The audit writer couldn\'t observe actual runtime injection and inferred from DB linkage alone. Means "this skill is LINKED to the agent but we cannot prove it was actually used on this turn." Properly-wired paths produce one of the labels above. Since 31 October 2026 it means only that no skill router ran on this path: a router that chose nothing writes no rows, and a router that is switched off writes Advertised.',
        },
      ]}
    />

    <SectionTitle>What an AuditFallback row really tells you</SectionTitle>
    <HelpParagraph>
      The runtime audit writer in <code>AICompletionService.TryWriteSkillLoadAuditAsync</code> has an
      inference fallback: when the LLM call completes without <code>options.LoadedSkills</code>{' '}
      having been populated upstream, it queries <code>ai.AiAgentSkill</code> directly and writes
      one row per linked skill. Those rows get tagged <code>AuditFallback</code> instead of the
      generic <code>AgentLink</code> they used to get — explicit honesty about "we don\'t know if
      these were really injected; we just know they\'re linked."
    </HelpParagraph>

    <HelpParagraph>
      An occasional <code>AuditFallback</code> row may be benign (e.g., a code path that doesn\'t
      run through our standard skill registration). A pattern of <code>AuditFallback</code> on
      every turn for a specific agent is a signal that that agent\'s execution path is bypassing
      the standard wiring. Look at App Insights traces for that agent\'s turn — if{' '}
      <code>[DatabaseSkillsProviderFactory] Created AgentSkillsProvider</code> appears, the
      runtime DID inject skills (this is a writer-side gap); if it doesn\'t, the runtime path
      genuinely never registered the provider (this is the bug pattern).
    </HelpParagraph>

    <SectionTitle>Backend dashboard query</SectionTitle>
    <CodeBlock
      language="sql"
      code={`-- Agents that are getting AuditFallback rows in the last hour
SELECT  AgentId,
        COUNT(*)            AS AuditFallbackRows,
        COUNT(DISTINCT PromptLogId) AS AffectedTurns,
        MIN(LoadedAtUtc)    AS FirstSeenUtc
FROM    ai.PromptLogSkillLoad
WHERE   LoadSource = 'AuditFallback'
  AND   LoadedAtUtc > DATEADD(HOUR, -1, SYSUTCDATETIME())
GROUP   BY AgentId
ORDER   BY AffectedTurns DESC;`}
    />

    <RelatedTopics
      items={[
        { topic: 'grounding.skill-injection', label: 'Did skills/KB reach the LLM?' },
        { topic: 'grounding.silent-skill-loss', label: 'Silent skill-loss bug' },
      ]}
    />
  </>
);

const GroundingSilentSkillLossBody = () => (
  <>
    <HeroBlock
      icon={<WarningOutlined />}
      title="The silent skill-loss bug"
      tagline="Six months of comfortably-wrong audit data, traced end-to-end on 2026-05-03. This page captures what we found so the next on-call engineer doesn't have to repeat the investigation."
      accent="amber"
    />

    <SectionTitle>What was happening</SectionTitle>
    <HelpParagraph>
      The prompt-review agent (and potentially any workflow-driven agent with linked skills) was
      reaching the LLM with NOTHING but its static <code>ai.AiAgent.Instructions</code> — no skill
      descriptors injected, no <code>load_skill</code> tool registered, no specialist rubric
      content available. <code>ToolCallCount</code> was 0 on every turn. The LLM was producing
      reasonable-looking reviews by pattern-matching on the dimension names that appeared in the
      bare instructions, but it had no access to the actual scoring criteria.
    </HelpParagraph>

    <HelpParagraph>
      Meanwhile, <code>ai.PromptLogSkillLoad</code> was reporting 12 rows per turn with{' '}
      <code>LoadSource = AlwaysLoad / AgentLink</code> — which looked like everything was wired
      correctly. Both signals were lying: the audit writer\'s inference fallback was inferring
      from DB linkage rather than actual injection.
    </HelpParagraph>

    <SectionTitle>Why it happened — the cascade</SectionTitle>
    <ChainDiagram
      nodes={[
        { label: 'new AIProcessManager(uow, logger)' },
        { label: 'new AICompletionService()' },
        { label: 'new AIWorkflowService(3-arg)', accent: 'amber' },
        { label: 'skillsProviderFactory = null', accent: 'amber' },
        { label: 'LLM call: bare Instructions', accent: 'red' },
      ]}
    />
    <HelpParagraph>
      Each step looks reasonable in isolation. The 2-arg <code>AIProcessManager</code> ctor falls
      back to a parameterless <code>AICompletionService</code>; that falls back to a 3-arg{' '}
      <code>AIWorkflowService</code> overload that doesn\'t accept the skills factory; that ctor
      runs with <code>_skillsProviderFactory = null</code>; and the if-guard at line ~631 silently
      skips the entire <code>AgentSkillsProvider</code> registration block. No exception, no log
      line, no obvious telemetry break. Just an LLM call with bare instructions.
    </HelpParagraph>

    <SectionTitle>Why telemetry didn't catch it</SectionTitle>
    <KeyValueList
      items={[
        {
          key: 'InputTokenCount',
          value: 'Looked normal because it billed the full conversation, including tool returns from later iterations. Wasn\'t a useful signal for "what\'s in the system prompt itself."',
        },
        {
          key: 'PromptLogSkillLoad rows',
          value: 'Showed 12 skills with AlwaysLoad/AgentLink labels — but those came from the audit writer\'s inference fallback inferring from DB linkage, NOT from actual runtime injection.',
        },
        {
          key: 'ToolCallCount = 0',
          value: 'Looked like the LLM just chose not to call tools. We initially attributed it to o3-mini\'s known weak tool-calling — but gpt-4.1 also produced 0, because there was no load_skill tool to call in the first place.',
        },
      ]}
    />

    <SectionTitle>How it&rsquo;s closed now</SectionTitle>
    <KeyValueList
      items={[
        {
          key: 'IWorkflowRuntime',
          value: 'New single front door for workflow execution. Owns IServiceScopeFactory; every workflow run gets a fresh DI scope so optional deps can\'t be silently dropped through hand-built construction.',
        },
        {
          key: 'AIWorkflowService self-bootstrap',
          value: 'Defensive: if a hand-built code path still slips through, the AIWorkflowService ctor self-constructs DatabaseSkillsProviderFactory from the UnitOfWork. Backstop for any future cascade we don\'t yet know about.',
        },
        {
          key: 'Two-hop audit propagation',
          value: 'The factory stamps options.LoadedSkills as it builds the AgentSkillsProvider; BaseExecutor re-stamps DefaultAIOptions with the same query because the build-time and call-time options are different instances. Both stamps run; both should produce identical SkillLoadRecord lists.',
        },
        {
          key: 'AuditFallback LoadSource',
          value: 'New honest label for the inference-fallback path. Properly-wired paths produce AlwaysLoad / AgentLink / RouterPicked rows; AuditFallback rows are the "investigate" signal documented in PromptLogSkillLoad.LoadSources.',
        },
        {
          key: 'SystemPromptTokens',
          value: 'New column on ai.PromptLog. Estimates the system prompt size at LLM call time. Compared to the agent\'s base Instructions length, this is the definitive "skills/KB injected: yes/no" signal — surfaced in the grounding panel on every LLM event.',
        },
      ]}
    />

    <Callout tone="warning" title="If you see this pattern again">
      Filter App Insights for the agent&rsquo;s turn:{' '}
      <code>traces | where customDimensions.message startswith "[DatabaseSkillsProviderFactory]"</code>{' '}
      &mdash; if those traces don&rsquo;t appear, the runtime never registered the provider. Then check{' '}
      <code>traces | where customDimensions.message startswith "[AIWorkflowService.ctor]"</code>{' '}
      to see whether the self-bootstrap fired. <code>injectedFactory=False, factoryReady=True</code>{' '}
      means the cascade hit a hand-built construction site but the self-bootstrap caught it. If
      you see <code>factoryReady=False</code>, that&rsquo;s a new gap &mdash; likely a code path with no
      UnitOfWork at the AIWorkflowService construction site.
    </Callout>

    <RelatedTopics
      items={[
        { topic: 'grounding.skill-injection', label: 'Did skills/KB reach the LLM?' },
        { topic: 'grounding.load-sources', label: 'LoadSource labels' },
      ]}
    />
  </>
);

// ── Instruction Lint (added 2026-05-03) ────────────────────────────────────

const LintOverviewBody = () => (
  <>
    <HeroBlock
      icon={<FilterOutlined />}
      title="Instruction Lint"
      tagline="Author-time quality checks for your agent's instructions. Like a spell-checker, but for grounding, clinical-safety, and authoring discipline. Catches issues before they reach a patient or clinician."
      accent="violet"
    />

    <HelpParagraph>
      Pick an agent at the top of the page; lint runs every applicable
      rule against the agent's current instruction body and shows you
      what fired. Each finding has a severity, a plain-English message,
      and (when the rule has a known fix) a snippet you can paste
      straight into the instruction editor.
    </HelpParagraph>

    <SectionTitle>How to read a finding</SectionTitle>
    <KeyValueList
      items={[
        {
          key: 'Blocking (red)',
          value:
            'Fired only when the agent declared EnforceKbLoad or EnforceSkillLoad and that category of rule failed. Save will be blocked in the agent editor until cleared. Treat as "must fix before shipping."',
        },
        {
          key: 'Advisory (amber)',
          value:
            'Yellow advisory. Strong recommendation — usually a citation, escalation, or anti-pattern call-out. Save proceeds; you can dismiss the advisory if you have a reason.',
        },
        {
          key: 'Info (blue)',
          value:
            'Friendly nudge. Often a suggestion to reference a linked skill or knowledge base your agent has but hasn\'t yet mentioned in its instruction body.',
        },
      ]}
    />

    <SectionTitle>The rule categories</SectionTitle>
    <KeyValueList
      items={[
        {
          key: 'Grounding',
          value:
            'Are linked skills referenced in the instructions? Are knowledge bases mentioned where they should be? Does the agent have an action-discipline section telling the LLM when to use tools? Catches "I attached a skill but never told the agent to use it" — a class of bug that produces silent under-grounding.',
        },
        {
          key: 'Clinical safety',
          value:
            'Citation policy present? Patient-escalation contract present? Drug-specific safety rules attached when the agent\'s scope mentions a tracked drug class? These are the rules that protect patients from confidently-wrong AI output.',
        },
        {
          key: 'Anti-patterns',
          value:
            'Authoring smells the platform has seen produce bad behavior — vague placeholders, tool/method-name leaks, charset corruption, missing audience tags. None are fatal; together they suggest the instruction body needs a polish pass.',
        },
      ]}
    />

    <SectionTitle>The "Suggested fix" snippet</SectionTitle>
    <HelpParagraph>
      Where a rule has a deterministic fix, lint pre-renders a Markdown
      snippet you can copy and paste into the instruction editor. The
      snippet is intentionally generic — read it, adapt it to your
      agent\'s tone and scope, and save. Lint re-runs on the next save
      and confirms the rule is now clean.
    </HelpParagraph>

    <Callout tone="info" title="What lint does NOT do">
      Lint checks the instruction <em>body</em> against rules it knows
      about. It doesn\'t verify your agent will actually behave correctly
      in production — that\'s what scenario validations are for. Treat
      lint as the spell-checker; treat scenario validations as the
      ground truth for behavior.
    </Callout>

    <RelatedTopics
      items={[
        { topic: 'agents.discoverable-abilities', label: 'Discoverable abilities' },
        { topic: 'grounding.skill-injection', label: 'Did skills reach the LLM?' },
        { topic: 'validations.overview', label: 'Agent validations' },
      ]}
    />
  </>
);

// ── Provisioning health — agent stack diagnostic ───────────────────────────

const ProvisioningHealthBody = () => (
  <>
    <HeroBlock
      icon={<HeartOutlined />}
      title="Provisioning health"
      tagline="A read-only check that walks through every layer of your agent's stack and tells you whether the agent is ready for production. Seven cards in one drawer — green, yellow, or red, each with a plain-English next step when something is missing."
      accent="ink"
    />

    <SectionTitle>What it checks</SectionTitle>
    <KeyValueList
      items={[
        {
          key: 'Agent record',
          value:
            'Does the agent row exist in the database? Are the required fields filled in (canonical name, instructions, reasoning level)? If the row is missing or partial, every other check fails — fix this first.',
        },
        {
          key: 'Foundry agent registration',
          value:
            'Is the agent registered in Azure AI Foundry (does ai.AiAgent.FoundryAgentId have a value)? Without registration, voice and multi-channel calls cannot reach the agent — only text-mode local middleware can.',
        },
        {
          key: 'Skills',
          value:
            'How many procedural skills are linked to the agent? Are any flagged "always-load" (cross-cutting safety frameworks)? An agent with zero linked skills will run on instructions alone — fine for narrow agents, a gap for clinical regimens.',
        },
        {
          key: 'Knowledge bases',
          value:
            'How many knowledge bases are linked? Linkage wires the SearchKnowledge tool — without a KB the agent cannot ground answers in your curated NCCN/ASCO/MASCC content. Acceptable for peer specialists; usually a gap for regimen patient agents.',
        },
        {
          key: 'Peer wirings',
          value:
            'Which oncology specialist peers are wired (Patient-Education, Pharmacy, Nutrition, Triage-Nurse, Mental-Health)? For oncology regimen patient agents we expect ALL FIVE — a missing peer means questions in that domain go unanswered or fall back to the regimen agent\'s own KB, which is exactly the behavior we want the peers to take over.',
        },
        {
          key: 'Test prompts',
          value:
            'How many test prompts are configured for adverse-event scoring? With zero, /validate-ae-prompts has nothing to grade and the agent has no automated regression coverage on common patient asks.',
        },
        {
          key: 'Validation scenarios',
          value:
            'How many multi-turn scenarios are active? The lifecycle stages are Smoke (3–5), Alpha (8–12), Beta (15–25), Production (≥25). Below Smoke, the agent has no regression coverage worth speaking of.',
        },
      ]}
    />

    <SectionTitle>How to read the badges</SectionTitle>
    <KeyValueList
      items={[
        {
          key: '✓ Healthy (green)',
          value:
            'The area is fully provisioned and looks correct. No action needed.',
        },
        {
          key: '⚠ Warning (yellow)',
          value:
            'The area is functional but missing something the operator may have intended. Example: only 3 of 5 oncology peers wired, or scenarios at "Smoke" coverage. Read the remediation hint and decide whether to fix now or later.',
        },
        {
          key: '✗ Failing (red)',
          value:
            'The area is not provisioned and blocks production readiness. Example: agent record missing, Foundry not registered. Fix before declaring the agent ready.',
        },
      ]}
    />

    <SectionTitle>What "ready for production" really means</SectionTitle>
    <HelpParagraph>
      Green across all seven checks is the floor, not the ceiling. The drawer
      verifies <em>provisioning</em> — that every layer of the stack exists.
      It does not verify <em>clinical correctness</em>; that's what scenario
      validations and clinical-SME review are for. Read this drawer first,
      then run /validate-agent-run, then have a clinical SME spot-check the
      HTML report. All three gates pass = production-grade.
    </HelpParagraph>

    <Callout tone="info" title="Safe to call repeatedly">
      The endpoint is read-only. Click Recheck as you wire peers, link
      knowledge bases, and add scenarios — the badges flip from yellow to
      green in real time. No mutations, no rate limit, no audit footprint.
    </Callout>

    <RelatedTopics
      items={[
        { topic: 'agents.overview', label: 'Agents' },
        { topic: 'grounding.skill-injection', label: 'Did skills reach the LLM?' },
        { topic: 'validations.overview', label: 'Agent validations' },
      ]}
    />
  </>
);

// ── Briefing Context — Elements ────────────────────────────────────────────
//
// SME-facing primer for the Element Catalog page. The agent's "patient
// briefing" is a stack of elements; this drawer explains what an element IS,
// how it's composed into briefings, and how Stable vs Per-turn affects cost
// and freshness. Written for clinical SMEs, not engineers — keep jargon out
// of the body and confine engineer-only signals to the Advanced view callout.

const BriefingElementsBody = () => (
  <>
    <HeroBlock
      icon={<ExperimentOutlined />}
      title="Briefing Elements"
      tagline="The building blocks of patient briefings. Each element gives the agent one fact about the patient — age, current treatment, recent labs, allergies — every turn of the conversation."
      accent="violet"
    />

    <HelpParagraph>
      An <b>element</b> is a single piece of patient context. Think of each
      element as a sticky note the agent reads before responding: <em>“Patient
      is 62, on FOLFOX, has a penicillin allergy.”</em> A <b>briefing</b> is
      the stack of sticky notes you give to one agent — every patient-facing
      regimen agent typically gets 5–15 elements.
    </HelpParagraph>

    <SectionTitle>The two refresh cadences</SectionTitle>
    <KeyValueList
      items={[
        {
          key: 'Stable',
          value:
            'Cached during the conversation. Rendered once on the first turn and reused for follow-ups — saves tokens. Use for things that don’t change minute-to-minute (allergies, primary diagnosis, language preference).',
        },
        {
          key: 'Per-turn',
          value:
            'Refreshed every message. Use for state that the agent must see as it changes (recent vitals, active symptoms, cycle day).',
        },
      ]}
    />

    <Callout tone="info" title="Cost vs freshness">
      Every element you mark Per-turn costs tokens on every message. Most
      elements should be Stable; reserve Per-turn for state where staleness
      would mislead the agent (e.g. a fever reported five minutes ago).
    </Callout>

    <SectionTitle>What a Preview shows</SectionTitle>
    <StepFlow
      steps={[
        {
          icon: <EyeOutlined />,
          title: 'Click Preview on any card',
          body: 'Opens a side drawer with three sections.',
        },
        {
          icon: <BulbOutlined />,
          title: '“What it does”',
          body: 'Plain-language explanation of what this element gives the agent — the why.',
        },
        {
          icon: <CodeOutlined />,
          title: '“What the agent sees”',
          body: 'A canonical one-line sample of the text injected into the agent’s prompt at runtime. Real renderings vary with the patient’s actual data.',
          accent: 'violet',
        },
        {
          icon: <MessageOutlined />,
          title: '“SME description”',
          body: 'The free-text description authored on this element row. Edit it from the card’s Edit button to tune what other SMEs see.',
          accent: 'green',
        },
      ]}
    />

    <SectionTitle>The kill switch</SectionTitle>
    <Callout tone="warning" title="One toggle, every briefing">
      Flipping an element’s <b>Active</b> switch off skips it everywhere — in
      every briefing, for every agent linked to those briefings, on the very
      next turn. Use this when an element is misbehaving (wrong data, broken
      renderer) and you need to take it out of circulation surgically.
    </Callout>

    <SectionTitle>Advanced view</SectionTitle>
    <HelpParagraph>
      The <b>Cards / Advanced</b> toggle at the top right reveals the
      engineering fields: stable <code>Code</code> (audit identifier), C#
      Renderer class, sort order, and renderer-drift warnings. SMEs rarely
      need these; engineers tuning the runtime do.
    </HelpParagraph>

    <RelatedTopics
      items={[
        { topic: 'briefing-context.briefings', label: 'Briefings (compose elements)', icon: <ReadOutlined /> },
        { topic: 'briefing-context.agent-link', label: 'Linking briefings to an agent', icon: <ProfileOutlined /> },
        { topic: 'agents.overview', label: 'Agents', icon: <RobotOutlined /> },
      ]}
    />
  </>
);

// ── Briefing Context — Briefings ───────────────────────────────────────────

const BriefingsBody = () => (
  <>
    <HeroBlock
      icon={<ReadOutlined />}
      title="Briefings"
      tagline="A named bundle of patient-state elements. Agents link to briefings; at runtime each briefing’s elements get rendered and injected into the system prompt."
      accent="violet"
    />

    <HelpParagraph>
      Where <em>elements</em> are individual sticky notes about a patient, a{' '}
      <b>briefing</b> is the binder that holds them in order. One agent
      typically links to 1–3 briefings: a generic safety briefing (allergies,
      advance directives), a chronic-condition briefing (e.g. diabetes
      context), and a regimen-specific briefing (e.g. FOLFOX patient
      context).
    </HelpParagraph>

    <SectionTitle>Lifecycle: Draft → Published</SectionTitle>
    <StepFlow
      steps={[
        {
          icon: <PlusOutlined />,
          title: 'Create as a draft',
          body: 'New briefings start as drafts. Drafts are invisible to the runtime — agents linked to a draft will not see any of its elements.',
        },
        {
          icon: <ExperimentOutlined />,
          title: 'Compose the elements',
          body: 'Open the briefing detail page → "Composition" section → "Add element". Pick one or more elements from the catalog. Order matters: lower-sort elements render first.',
          accent: 'violet',
        },
        {
          icon: <CloudUploadOutlined />,
          title: 'Publish',
          body: 'Promotes the draft to published, stamps PublishedAt, and bumps Version. Re-publishing after edits is the normal flow — Version goes up each time so audit rows can trace which version a turn ran against.',
          accent: 'green',
        },
      ]}
    />

    <SectionTitle>Global vs Merchant-private</SectionTitle>
    <KeyValueList
      items={[
        {
          key: 'Global',
          value: 'No MerchantId set. Visible to every merchant unless they author a private override with the same Code.',
        },
        {
          key: 'Merchant-private',
          value: 'MerchantId set. Visible only to that merchant; wins over a global briefing with the same Code if both exist.',
        },
      ]}
    />

    <SectionTitle>Composition: per-element token override</SectionTitle>
    <HelpParagraph>
      Each composition row carries an optional <b>token budget override</b>.
      If unset, the element’s default budget applies. Use the override when a
      briefing needs more (or less) of a particular element — for example, a
      pharmacy briefing often raises <code>state.active-medications</code>{' '}
      from the default 200 to 400 so the full medication list fits.
    </HelpParagraph>

    <Callout tone="info" title="Tier-1 elements render before Tier-2">
      The composition editor groups elements by tier with a visual divider.
      Within each tier, sort order decides render order. The runtime always
      emits Stable (Tier-1) elements before Per-turn (Tier-2) ones.
    </Callout>

    <SectionTitle>Kill switch</SectionTitle>
    <HelpParagraph>
      Flipping a briefing’s <b>Active</b> toggle off removes it from every
      agent that links it — on the very next turn. The agent stays linked
      (history preserved), but the runtime skips the briefing entirely.
    </HelpParagraph>

    <RelatedTopics
      items={[
        { topic: 'briefing-context.elements', label: 'Briefing elements (building blocks)', icon: <ExperimentOutlined /> },
        { topic: 'briefing-context.agent-link', label: 'Linking briefings to an agent', icon: <ProfileOutlined /> },
      ]}
    />
  </>
);

// ── Briefing Context — Agent's Patient Briefing tab ────────────────────────

const AgentBriefingBody = () => (
  <>
    <HeroBlock
      icon={<ProfileOutlined />}
      title="Patient Briefing (on an agent)"
      tagline="The set of briefings this agent receives every turn. Manage the links here — add, remove, kill-switch, or reorder for element-dedup precedence."
      accent="violet"
    />

    <HelpParagraph>
      An agent’s <b>Patient Briefing</b> tab is a list of briefing-to-agent
      links. Each link points at one published briefing; at chat time the
      runtime walks the active links in sort order, expands each briefing’s
      composition, and injects the rendered elements into the system prompt.
    </HelpParagraph>

    <SectionTitle>Order matters: dedup precedence</SectionTitle>
    <Callout tone="info" title="Lower sort wins">
      If two linked briefings both contain the same element code (e.g. both
      include <code>clinical.allergies</code>), the one with the{' '}
      <b>lower SortOrder</b> wins. The other occurrence is skipped — the
      runtime logs the skip in <code>ai.PromptLogBriefingContext</code> for
      traceability. Use the up/down arrows to reorder so the briefing whose
      version of an element you want takes precedence.
    </Callout>

    <SectionTitle>Two kinds of switch-off</SectionTitle>
    <KeyValueList
      items={[
        {
          key: 'Per-link toggle',
          value: 'Per-row Active switch. Soft-disable for one briefing on this agent only. Row is preserved so re-enabling is one click.',
        },
        {
          key: 'Hard unlink (trash)',
          value: 'Delete the link row entirely. Use when you don’t expect to re-enable. The briefing itself stays in the catalog.',
        },
        {
          key: 'Disable all',
          value: 'On-call panic button. Flips every linked briefing on this agent to inactive in one step. Links are preserved — re-enable individually later.',
        },
      ]}
    />

    <SectionTitle>Linking a briefing</SectionTitle>
    <StepFlow
      steps={[
        {
          icon: <LinkOutlined />,
          title: 'Click "+ Link briefing"',
          body: 'Opens the briefing picker drawer with all published briefings the agent can link.',
        },
        {
          icon: <ReadOutlined />,
          title: 'Click a row to link',
          body: 'One click links the briefing and closes the drawer. Already-linked briefings are shown muted with a "Linked" badge.',
          accent: 'violet',
        },
        {
          icon: <SortAscendingOutlined />,
          title: 'Reorder via the arrows',
          body: 'Once you have multiple links, use the up/down arrows in the Order column to set element-dedup precedence.',
        },
        {
          icon: <PoweroffOutlined />,
          title: '"Disable all" when needed',
          body: 'Master kill switch in the toolbar. Confirms before flipping every link inactive. Designed for on-call use when patient context needs to be cut.',
          accent: 'amber',
        },
      ]}
    />

    <SectionTitle>What "draft" warnings mean</SectionTitle>
    <HelpParagraph>
      If an active link points at a briefing that is currently a draft (or
      kill-switched off), the tab shows an inline warning. The runtime would
      silently skip that briefing — better to surface the mismatch so you can
      either publish/enable the briefing or unlink it here.
    </HelpParagraph>

    <RelatedTopics
      items={[
        { topic: 'briefing-context.briefings', label: 'Briefings', icon: <ReadOutlined /> },
        { topic: 'briefing-context.elements', label: 'Briefing elements', icon: <ExperimentOutlined /> },
        { topic: 'agents.overview', label: 'Agents', icon: <RobotOutlined /> },
      ]}
    />
  </>
);

// ── Registry ───────────────────────────────────────────────────────────────

export const helpTopics: Record<string, HelpTopic> = {
  'insights.overview': { label: 'Insights', Body: InsightsOverviewBody },
  'knowledge.rag': { label: 'Knowledge & RAG', Body: KnowledgeRagBody },
  'prompts.prebuilt': { label: 'Prebuilt prompts', Body: PrebuiltPromptsBody },
  'prompts.system': { label: 'System prompts', Body: SystemPromptsBody },
  'agents.overview': { label: 'Agents', Body: AgentsOverviewBody },
  'agents.discoverable-abilities': { label: 'Discoverable abilities', Body: DiscoverableAbilitiesBody },
  'agents.audience': { label: 'Audience', Body: AudienceBody },
  'agents.skill-router': { label: 'Skill router', Body: SkillRouterBody },
  'workflows.overview': { label: 'Workflows', Body: WorkflowsBody },
  'procedures.skills': { label: 'Skills', Body: ProceduresSkillsBody },
  'tools.plugins': { label: 'AI plugins (tools)', Body: ToolsPluginsBody },
  'governance.action-policies': { label: 'Action policies', Body: ActionPoliciesHelpBody },
  'validations.overview': { label: 'Agent validations', Body: ValidationsBody },
  'validations.scenarios.library': { label: 'Scenario library', Body: ScenarioLibraryHelpBody },
  'validations.scenarios.filter': { label: 'Filtering scenarios', Body: ScenarioFilterHelpBody },
  'validations.scenarios.detail': { label: 'Scenario detail', Body: ScenarioDetailHelpBody },
  'validations.runs.recent': { label: 'Recent runs', Body: RecentRunsHelpBody },
  'validations.runs.detail': { label: 'Run detail', Body: RunDetailHelpBody },
  'costs.overview': { label: 'Costs', Body: CostsHelpBody },
  'costs.coverage': { label: 'Is the number complete?', Body: CostsCoverageHelpBody },
  'costs.trend': { label: 'Spend over time', Body: CostsTrendHelpBody },
  'costs.agents': { label: 'Top agents by spend', Body: CostsAgentsHelpBody },
  'costs.per-run': { label: 'Cost per run', Body: CostsPerRunHelpBody },
  'costs.unusual': { label: 'Unusual runs', Body: CostsUnusualHelpBody },
  'models.overview': { label: 'Models', Body: ModelsBody },
  'merchants.overview': { label: 'Merchants', Body: MerchantsBody },
  'audit.overview': { label: 'Audit log', Body: AuditBody },

  // ── Grounding-signal topics (added 2026-05-03) ────────────────────────
  // Linked from the GroundingPanel inside ActivityDrawer + the diagnosis
  // transcript captured here so the next on-call doesn't have to redo
  // the six-month root-cause investigation we just finished.
  'grounding.skill-injection': {
    label: 'Did skills/KB reach the LLM?',
    Body: GroundingSkillInjectionBody,
  },
  'grounding.load-sources': {
    label: 'LoadSource labels',
    Body: GroundingLoadSourcesBody,
  },
  'grounding.silent-skill-loss': {
    label: 'Silent skill-loss bug',
    Body: GroundingSilentSkillLossBody,
  },

  // Instruction Lint — agent authoring quality checks. Linked from the
  // PageHeader on the InstructionLint page so a clinical SME or designer
  // can read what the page is for before picking an agent to run.
  'lint.overview': {
    label: 'Instruction Lint',
    Body: LintOverviewBody,
  },

  // Provisioning health — operational diagnostic for the agent's full
  // stack. Linked from the "Provisioning health" button on the Agent
  // detail page header. Read this BEFORE declaring an agent production-grade.
  'provisioning.health': {
    label: 'Provisioning health',
    Body: ProvisioningHealthBody,
  },

  // Patient Briefing Context — three SME-facing surfaces. The Element
  // Catalog page, the Briefings page (list + detail), and the per-agent
  // Patient Briefing tab. Topics are cross-linked via RelatedTopics so an
  // SME landing on any one of them can navigate to the related concepts.
  'briefing-context.elements': {
    label: 'Briefing elements',
    Body: BriefingElementsBody,
  },
  'briefing-context.briefings': {
    label: 'Briefings',
    Body: BriefingsBody,
  },
  'briefing-context.agent-link': {
    label: 'Patient Briefing on an agent',
    Body: AgentBriefingBody,
  },
};
